import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { Button, Box, FormControl, FormLabel, Stack, Input, DialogActions, Theme, alpha } from '@mui/material';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import React, { useState, useEffect, useMemo } from 'react';
import useBundleTranslation from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import IconMi from 'components/common/icon/IconMi';
import { Params as UrlParams } from '@remix-run/router/dist/utils';
import {
    BuildUrlRequestDataType,
    editorExternalReportAPI,
    ExternalReportTypeResponse,
    ExternalReportBuildUrlResponse,
} from 'api/editor/external-report';
import CustomUrlForm from './CustomUrlForm';
import ExternalUrlForm from './ExternalUrlForm';
import { setReportTypesData } from 'store/externalReport';
import { generateFullURL, getTails, getUrlAppend, formatUrl } from './helpers';
import { processSettingsUrl } from 'components/common/form/formTools';
import styles from './ExternalReportCustomUrl.styles';
import { openInNewTab } from 'tools/tools';
import { FormComponentBuilder } from 'components/common/form/layout';

export interface ExternalReportCustomUrlControlProps extends FormControlProps {
    urlParams: UrlParams;
    buildUrl: string;
    mode: 'auto' | 'manual';
    template_name: string;
    plugin: string;
    report_type_id: string;
    report_display: string;
    show_test_url: boolean;
}

const checkIsAllParamsExists = (params: UrlParams) => {
    return Number(params.reference) !== 0 && Number(params.template) !== 0 && Number(params.profile) !== 0;
};

interface IReportType {
    report_url_external_page_append: string;
    report_url_viewer_append: string;
}

export default function ExternalReportCustomUrl({
    controlProps,
}: FormElementControlPropsType<ExternalReportCustomUrlControlProps>) {
    const dispatch = useDispatch();

    // Default External Report URL
    const [urlDefaultValue, setUrlDefaultValue] = useState();
    useEffect(() => {
        if (!controlProps.form.formDidMount) {
            return;
        }
        setUrlDefaultValue(controlProps.form.hookFormGetValues('external_report_url'));
    }, [controlProps.form.formDidMount]);

    const [value, setValue] = useState(controlProps.form.hookFormGetValues('external_report_url'));
    const [fullUrl, setFullUrl] = useState('');
    const [appendFromSetting, setAppendFromSetting] = useState('');
    const [urlFormActive, setUrlFormActive] = useState<boolean>(false);
    const { t } = useBundleTranslation(['app/editor/element/extreport']);
    const reportTypesState = useSelector((state: any) => state.externalReport.reportTypes);

    const append = useMemo((): string => {
        const data = reportTypesState[controlProps.report_type_id] ?? {};

        return getUrlAppend(controlProps.report_display, data);
    }, [reportTypesState, controlProps.report_type_id]);

    const tableauToolbar = controlProps.form.hookFormWatch('external_report_tableau_toolbar');
    const customUrlTail = controlProps.form.hookFormWatch('external_report_custom_url_tail_ind');
    const tableauTabsInd = controlProps.form.hookFormWatch('external_report_tableau_tabs_ind');
    const useCustomTail = 'tableau' === controlProps.plugin && 'Y' === customUrlTail;

    const [isAutoGeneration, setIsAutoGeneration] = useState(false);
    useEffect(() => {
        setIsAutoGeneration(checkIsAllParamsExists(controlProps.urlParams) && controlProps.mode === 'auto');
    }, [JSON.stringify(controlProps.urlParams), controlProps.mode]);

    const setupImgUrl = processSettingsUrl(
        controlProps.buildUrl,
        Object.keys(controlProps.urlParams),
        controlProps.urlParams,
    );

    const params: BuildUrlRequestDataType = {
        profile: Number(controlProps.urlParams?.profile ?? 0),
        reference: Number(controlProps.urlParams?.reference ?? 0),
        template: Number(controlProps.urlParams?.template ?? 0),
    };

    const { data: urlData, refetch } = useQuery<ExternalReportBuildUrlResponse | any, Error>({
        queryKey: ['customUrlForm_BuildUrl', setupImgUrl, params],
        queryFn: () => editorExternalReportAPI.buildUrl(setupImgUrl, params),
    });

    const [urlTail, setUrlTail] = useState('');

    // Actual URL without tail from server
    const [baseUrl, setBaseUrl] = useState();
    useEffect(() => {
        if (!(isAutoGeneration && urlData?.uri)) {
            return;
        }
        setBaseUrl(urlData.uri);
    }, [urlData, isAutoGeneration]);

    useEffect(() => {
        if (!baseUrl) {
            return;
        }
        // Match actual external URL with new baseUrl
        const actualUrl: string = controlProps.form.hookFormGetValues('external_report_url');
        let tail = '';
        if (actualUrl.includes(baseUrl)) {
            // If baseUrl is a part of actualURL, consider extra characters in actualUrl as tail
            // else ignore actualURL and replace it with baseUrl
            tail = getTails(actualUrl, baseUrl);
        }
        setUrlTail(tail);
    }, [baseUrl]);

    // Update ExternalReportUrl on tail change
    useEffect(() => {
        if (!baseUrl) {
            return;
        }
        let newUrl = baseUrl as string;

        const suffix = -1 == newUrl.indexOf('?') ? '?' : '&';

        const processedTail = urlTail[0] === '?' || urlTail[0] === '&' ? urlTail.slice(1) : urlTail;

        if (urlTail > '') {
            newUrl += suffix + processedTail;
        }

        controlProps.form.hookFormSetValue('external_report_url', newUrl);
    }, [baseUrl, urlTail]);

    const handleCloseUrlForm = () => {
        setFullUrl('');
        setUrlFormActive(false);
    };

    const [reportTypeData, setReportTypeData] = useState<IReportType>();
    const { data: reportTypeDataResponse, refetch: getReportTypeData } = useQuery<
        ExternalReportTypeResponse | any,
        Error
    >({
        queryKey: [`customUrlForm_getTypesData_${controlProps.report_type_id}`, setupImgUrl, params],
        queryFn: () => editorExternalReportAPI.getReportTypeData(controlProps.report_type_id),
        enabled: false,
    });

    useEffect(() => {
        if (!reportTypeDataResponse) {
            return;
        }
        dispatch(
            setReportTypesData({
                data: reportTypeDataResponse,
                reportTypeId: controlProps.report_type_id,
            }),
        );
        setReportTypeData(reportTypeDataResponse);
    }, [reportTypeDataResponse]);

    useEffect(() => {
        refetch();
    }, [controlProps.urlParams.reference]);

    useEffect(() => {
        const reportTypeStateData = reportTypesState[controlProps.report_type_id];

        if (urlFormActive && controlProps.report_type_id) {
            if (!reportTypeData) {
                getReportTypeData();
            } else {
                setReportTypeData(reportTypeStateData);
            }
        }
    }, [controlProps.report_type_id, urlFormActive]);

    const clickTestUrlButton = () => {
        openInNewTab(value);
    };

    const [customAppend, setCustomAppend] = useState('');
    useEffect(() => {
        const subscription = controlProps.form.hookFormWatch((value, { name, type }) => {
            if (name === 'custom_append') {
                setCustomAppend(value['custom_append']);
            }
        });

        return () => subscription?.unsubscribe();
    }, [controlProps.form.hookFormWatch]);

    useEffect(() => {
        if (!reportTypeData) {
            return;
        }

        if (fullUrl.includes(urlData?.uri)) {
            return;
        }

        const { fullUrl: newFullUrl } = generateFullURL({
            useCustomTail,
            tableauToolbar,
            url: urlData?.uri,
            append: getUrlAppend(controlProps.report_display, reportTypeData),
            custom: '',
            tableauTabsInd,
        });

        setFullUrl(newFullUrl);
    }, [reportTypeData, urlData?.uri, urlFormActive]);

    const formattedUrl = useMemo((): string => {
        let foundedIndex = fullUrl.indexOf('??');

        if (foundedIndex === -1) {
            foundedIndex = fullUrl.indexOf('?:') !== -1 ? fullUrl.indexOf('?:') : fullUrl.indexOf(append);
        }

        if (append === '' && foundedIndex === 0) {
            foundedIndex = fullUrl.length;
        }

        if (foundedIndex !== -1) {
            const string = customAppend.startsWith('?')
                ? fullUrl.slice(foundedIndex).replace('?', '&')
                : fullUrl.slice(foundedIndex);

            return fullUrl.slice(0, foundedIndex) + customAppend + string;
        }
        const replacedUrl = fullUrl.replace(/\?+/gi, '?');
        const indexOfMark = replacedUrl.indexOf('?');

        return replacedUrl.slice(0, indexOfMark) + replacedUrl.slice(indexOfMark + 1).replace(/\?/g, '&');
    }, [fullUrl, customAppend.length, urlData?.uri]);

    const testUrlLink = useMemo((): string => {
        const currentBase = urlData?.uri ?? '';

        const suffix = -1 == currentBase.indexOf('?') ? '?' : '&';
        const processedTail = customAppend[0] === '?' || customAppend[0] === '&' ? customAppend.slice(1) : customAppend;

        const { fullUrl: testFullUrl } = generateFullURL({
            useCustomTail,
            tableauToolbar,
            url: urlData?.uri,
            append: appendFromSetting,
            custom: suffix + processedTail,
            tableauTabsInd,
        });

        return testFullUrl;
    }, [customAppend.length, urlData?.uri, appendFromSetting]);

    useEffect(() => {
        if (reportTypeDataResponse) {
            const settingsAppend = getUrlAppend(controlProps.report_display, reportTypeDataResponse);

            setAppendFromSetting(settingsAppend);
        }
    }, [reportTypeDataResponse]);

    const dialogActions = (
        <DialogActions sx={styles.dialogContainer}>
            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                <Button
                    component={'a'}
                    href={formatUrl(testUrlLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="light"
                    startIcon={<IconMi icon="test" />}
                >
                    {t('test_url')}
                </Button>
                <Box>
                    <Box sx={styles.divider} />
                </Box>
                <Button
                    variant={'contained'}
                    onClick={() => {
                        setUrlTail(controlProps.form.hookFormGetValues('custom_append'));
                        handleCloseUrlForm();
                    }}
                >
                    {t('done')}
                </Button>
                <Button variant={'outlined'} onClick={handleCloseUrlForm} color={'neutral'}>
                    {t('cancel')}
                </Button>
            </Stack>
        </DialogActions>
    );

    const popupSettings: PopupSettings = {
        title: isAutoGeneration ? t('custom_url') : t('report_url'),
        customActionPanel: dialogActions,
        subTitle: t(isAutoGeneration ? 'custom_url_subtitle' : 'external_url_title'),
    };

    return controlProps.mode === 'auto' ? (
        <>
            <Button onClick={() => setUrlFormActive(true)} variant="light" startIcon={<IconMi icon="edit-pencil" />}>
                {t('edit_url')}
            </Button>
            <Popup maxWidth="popupLg" settings={popupSettings} onHide={handleCloseUrlForm} open={urlFormActive}>
                {urlFormActive && (
                    <>
                        {isAutoGeneration ? (
                            <CustomUrlForm
                                key="custom_url"
                                append={append}
                                appendFromSetting={''}
                                customAppend={urlTail}
                                templateName={controlProps.template_name}
                                generatedUrl={urlData?.uri}
                                controlProps={controlProps}
                                formattedUrl={testUrlLink}
                            />
                        ) : (
                            <ExternalUrlForm
                                key="textarea_url"
                                append={append}
                                form={controlProps.form}
                                url={value}
                                onChange={setValue}
                            />
                        )}
                    </>
                )}
            </Popup>
        </>
    ) : (
        <FormControl fullWidth>
            <FormLabel className={controlProps.labelClass}>{t('url')}</FormLabel>
            <Stack direction={'row'}>
                <Input
                    name={'custom_url'}
                    value={value}
                    onChange={(event) => {
                        setValue(event.target.value);
                        controlProps.form.hookFormSetValue(controlProps.name, event.target.value, {
                            shouldDirty: true,
                        });
                        controlProps.form.hookFormSetValue('external_report_url', event.target.value);
                    }}
                    fullWidth
                    sx={{
                        ...styles.field,
                        ...(controlProps.show_test_url ? styles.fieldWithTestButton : {}),
                        backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
                    }}
                    inputProps={{ 'data-1p-ignore': true }}
                />
                {controlProps.show_test_url && (
                    <Button
                        data-test={controlProps.name + '-test-url'}
                        variant="contained"
                        startIcon={<IconMi icon="test" />}
                        disabled={value === ''}
                        onClick={clickTestUrlButton}
                        sx={styles.btn}
                    >
                        {t('test')}
                    </Button>
                )}
            </Stack>
        </FormControl>
    );
}

export class ExternalReportCustomUrlBuilder extends FormComponentBuilder {
    prepareProps(): ExternalReportCustomUrlControlProps {
        return {
            ...this.controlProps,
            urlParams: {
                ...this.elementProps.urlParams,
                ...this.elementProps.componentProps?.urlParams,
            } as UrlParams,
            mode: this.elementProps.componentProps?.mode ?? '',
            buildUrl: this.elementProps.componentProps?.buildUrl ?? '',
            plugin: this.elementProps.componentProps?.plugin ?? '',
            report_type_id: this.elementProps.componentProps?.report_type_id ?? '',
            report_display: this.elementProps.componentProps?.report_display ?? '',
            template_name: this.elementProps.componentProps?.template_name ?? '',
            show_test_url: this.elementProps.componentProps?.show_test_url ?? true,
        };
    }
}
